import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);
import { useAuth } from '../contexts/AuthContext';
import { 
  PlusIcon, 
  TicketIcon,
  ExclamationTriangleIcon,
  ExclamationCircleIcon,
  FireIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import config from '../config/api';

const Support = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM',
    type: 'SUPPORT',
    project_id: null
  });
  const [newComment, setNewComment] = useState('');
  const [projects, setProjects] = useState([]);
  const [isPublic, setIsPublic] = useState(true);
  const [isInternal, setIsInternal] = useState(false);
  const [comments, setComments] = useState([]);

  // Load tickets and projects on mount
  useEffect(() => {
    loadTickets();
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      console.log('🔄 Loading projects...');
      const endpoint = config.PROJECTS_URL;
      try {
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'no-token'}`
          }
        });
        console.log(`📡 Projects response from ${endpoint}:`, response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Projects loaded:', data);
          setProjects(data);
          return;
        }
      } catch (error) {
        console.error(`💥 Error loading projects from ${endpoint}:`, error);
      }
      console.log('⚠️ No projects endpoint working, using empty list');
      setProjects([]);
    } catch (error) {
      console.error('💥 Error loading projects:', error);
      setProjects([]);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading tickets...');
      
      const endpoint = config.MY_TICKETS_URL;
      try {
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'no-token'}`
          }
        });
        
        console.log(`📡 Tickets response from ${endpoint}:`, response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Tickets loaded:', data);
          setTickets(data);
          return;
        }
      } catch (error) {
        console.error(`💥 Error loading tickets from ${endpoint}:`, error);
      }
      
      console.log('⚠️ No tickets endpoint working, using empty list');
      setTickets([]);
      
    } catch (error) {
      console.error('💥 Error loading tickets:', error);
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      console.log('🔄 Creating ticket...', newTicket);
      
      const endpoint = config.SUBMIT_CUSTOMER_TICKET_URL;
      let success = false;
      
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token') || 'no-token'}`
          },
          body: JSON.stringify(newTicket)
        });
        
        console.log(`📡 Create ticket response from ${endpoint}:`, response.status);
        
        if (response.ok) {
          const ticket = await response.json();
          console.log('✅ Ticket created:', ticket);
          setTickets([ticket, ...tickets]);
          setNewTicket({
            title: '',
            description: '',
            priority: 'MEDIUM',
            type: 'SUPPORT',
            project_id: null
          });
          setShowCreateForm(false);
          success = true;
        } else {
          const errorText = await response.text();
          console.error(`❌ Error from ${endpoint}:`, errorText);
        }
      } catch (error) {
        console.error(`💥 Network error ${endpoint}:`, error);
      }
      
      if (!success) {
        alert('Không thể tạo ticket. Vui lòng thử lại sau.');
      }
      
    } catch (error) {
      console.error('💥 Error creating ticket:', error);
      alert('Lỗi tạo ticket: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load comments for a ticket, filter out internal comments for customers
  const loadComments = async (ticketId) => {
    try {
      const url = `${config.TASKS_API_URL}/tasks/${ticketId}/comments`;
      const urlNoApi = url.replace('/api', '');
      const response = await fetch(urlNoApi, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        let data = await response.json();
        // Nếu user là khách hàng thì chỉ hiển thị bình luận không phải nội bộ
        if (!user || user.role !== 'staff') {
          data = data.filter((c) => !c.is_internal);
        }
        setComments(data);
      } else {
        setComments([]);
      }
    } catch (error) {
      setComments([]);
      console.error('Error loading comments:', error);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTicket) return;
    setLoading(true);
    try {
      const url = `${config.TASKS_API_URL}/tasks/${selectedTicket.id}/comments`;
      const urlNoApi = url.replace('/api', '');
      const response = await fetch(urlNoApi, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          task_id: selectedTicket.id,
          comment: newComment,
          is_internal: isInternal,
          is_public: isPublic
        })
      });
      if (response.ok) {
        const comment = await response.json();
        setNewComment('');
        await loadComments(selectedTicket.id);
        await loadTickets();
      } else {
        const error = await response.json();
        alert('Lỗi thêm bình luận: ' + error.detail);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Lỗi thêm bình luận');
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'DRAFT': 'Bản nháp',
      'OPEN': 'Mở',
      'PROCESSING': 'Đang xử lý',
      'CLOSE': 'Đã đóng',
      'REOPEN': 'Mở lại'
    };
    return statusMap[status] || status;
  };

  const getPriorityDisplay = (priority) => {
    const priorityMap = {
      'LOW': 'Thấp',
      'MEDIUM': 'Trung bình',
      'HIGH': 'Cao',
      'URGENT': 'Khẩn cấp'
    };
    return priorityMap[priority] || priority;
  };

  const getTypeDisplay = (type) => {
    const typeMap = {
      'SUPPORT': 'Hỗ trợ',
      'BUG': 'Báo lỗi',
      'FEATURE': 'Yêu cầu tính năng',
      'OTHER': 'Khác'
    };
    return typeMap[type] || type;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'LOW': 'text-gray-600 bg-gray-100',
      'MEDIUM': 'text-blue-600 bg-blue-100',
      'HIGH': 'text-yellow-600 bg-yellow-100',
      'URGENT': 'text-red-600 bg-red-100'
    };
    return colors[priority] || 'text-gray-600 bg-gray-100';
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'text-gray-600 bg-gray-100',
      'OPEN': 'text-blue-600 bg-blue-100',
      'PROCESSING': 'text-yellow-600 bg-yellow-100',
      'CLOSE': 'text-green-600 bg-green-100',
      'REOPEN': 'text-orange-600 bg-orange-100'
    };
    return colors[status] || 'text-gray-600 bg-gray-100';
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'LOW':
        return <ClockIcon className="w-4 h-4" />;
      case 'MEDIUM':
        return <ExclamationCircleIcon className="w-4 h-4" />;
      case 'HIGH':
        return <ExclamationTriangleIcon className="w-4 h-4" />;
      case 'URGENT':
        return <FireIcon className="w-4 h-4" />;
      default:
        return <ClockIcon className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Always show GMT+7 explicitly
    return dayjs.utc(dateString).tz('Asia/Ho_Chi_Minh').format('HH:mm DD/MM/YYYY') + ' GMT+7';
  };

  // Khi chọn ticket, load lại comments mới nhất
  useEffect(() => {
    if (selectedTicket && selectedTicket.id) {
      loadComments(selectedTicket.id);
    } else {
      setComments([]);
    }
  }, [selectedTicket && selectedTicket.id]);

  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back button */}
          <button
            onClick={() => setSelectedTicket(null)}
            className="mb-6 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            ← Quay lại danh sách
          </button>

          {/* Ticket details */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  #{selectedTicket.id} - {selectedTicket.title}
                </h1>
                <div className="flex flex-wrap gap-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedTicket.status)}`}>
                    {getStatusDisplay(selectedTicket.status)}
                  </span>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                    {getPriorityIcon(selectedTicket.priority)}
                    <span className="ml-1">{getPriorityDisplay(selectedTicket.priority)}</span>
                  </span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-gray-600 bg-gray-100">
                    {getTypeDisplay(selectedTicket.type)}
                  </span>
                </div>
              </div>
              <div className="text-right text-sm text-gray-500">
                <div>Tạo: {formatDate(selectedTicket.created_at)}</div>
                <div>Cập nhật: {formatDate(selectedTicket.updated_at)}</div>
              </div>
            </div>
            
            {selectedTicket.description && (
              <div className="mb-4">
                <h3 className="font-medium text-gray-900 mb-2">Mô tả:</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>
            )}
          </div>

          {/* Comments */}
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              <ChatBubbleLeftRightIcon className="w-5 h-5 inline mr-2" />
              Trao đổi
            </h3>
            
            <div className="space-y-4 mb-6">
              {comments.length === 0 ? (
                <div className="text-gray-500 text-sm">Chưa có bình luận nào</div>
              ) : (
                comments.map((comment) => (
                  <div key={comment.id} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-sm text-gray-900">
                        {comment.user_id
                          ? (comment.user_name || 'Nhân viên hỗ trợ')
                          : 'Bạn'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(comment.created_at)}
                      </div>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{comment.comment}</p>
                  </div>
                ))
              )}
            </div>

            {/* Add comment form */}
            {selectedTicket.status !== 'CLOSE' && (
              <form onSubmit={handleAddComment}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thêm bình luận
                  </label>
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Nhập bình luận của bạn..."
                    required
                  />
                </div>
                {/* Chỉ hiển thị cho nhân viên, khách hàng không cần chọn loại */}
                {user && user.role === 'staff' && (
                  <div className="flex items-center gap-4 mb-2">
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={isPublic}
                        onChange={() => { setIsPublic(true); setIsInternal(false); }}
                      />
                      Công khai với khách hàng
                    </label>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        checked={isInternal}
                        onChange={() => { setIsPublic(false); setIsInternal(true); }}
                      />
                      Bình luận nội bộ
                    </label>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !newComment.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {loading ? 'Đang gửi...' : 'Gửi bình luận'}
                </button>
                {selectedTicket.status === 'CLOSE' && (
                  <p className="mt-2 text-sm text-orange-600">
                    * Ticket sẽ được mở lại tự động khi bạn gửi bình luận
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Hỗ trợ kỹ thuật</h1>
              <p className="text-gray-600">Quản lý các yêu cầu hỗ trợ và báo cáo sự cố</p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Tạo ticket mới
            </button>
          </div>
        </div>

        {/* Create ticket form modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-screen overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Tạo ticket hỗ trợ mới</h3>
              </div>
              
              <form onSubmit={handleCreateTicket} className="px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tiêu đề *
                  </label>
                  <input
                    type="text"
                    value={newTicket.title}
                    onChange={(e) => setNewTicket({...newTicket, title: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Mô tả ngắn gọn vấn đề"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mô tả chi tiết *
                  </label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                    rows={4}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Mô tả chi tiết vấn đề và các bước tái hiện (nếu có)"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Loại
                    </label>
                    <select
                      value={newTicket.type}
                      onChange={(e) => setNewTicket({...newTicket, type: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="SUPPORT">Hỗ trợ</option>
                      <option value="BUG">Báo lỗi</option>
                      <option value="FEATURE">Yêu cầu tính năng</option>
                      <option value="OTHER">Khác</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mức độ ưu tiên
                    </label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="LOW">Thấp</option>
                      <option value="MEDIUM">Trung bình</option>
                      <option value="HIGH">Cao</option>
                      <option value="URGENT">Khẩn cấp</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dự án liên quan (tùy chọn)
                  </label>
                  <select
                    value={newTicket.project_id || ''}
                    onChange={(e) => setNewTicket({...newTicket, project_id: e.target.value || null})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Chọn dự án --</option>
                  {projects.length === 0 ? (
                    <option value="" disabled>Không có dự án (cần đăng nhập)</option>
                  ) : (
                    projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name || project.title || `Dự án #${project.id}`}
                      </option>
                    ))
                  )}
                  </select>
                  {projects.length === 0 && (
                    <div className="text-xs text-gray-500 mt-1">
                      ⚠️ Không load được danh sách dự án. Có thể cần đăng nhập hoặc backend chưa sẵn sàng.
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {loading ? 'Đang tạo...' : 'Tạo ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tickets list */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              <TicketIcon className="w-5 h-5 inline mr-2" />
              Danh sách ticket
            </h2>
          </div>
          
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500">Đang tải...</p>
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-6 text-center">
              <TicketIcon className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">Chưa có ticket nào</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  onClick={async () => {
                    setSelectedTicket(ticket);
                    await loadComments(ticket.id);
                  }}
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-sm font-medium text-gray-900">
                          #{ticket.id} - {ticket.title}
                        </h3>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                          {getStatusDisplay(ticket.status)}
                        </span>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                          {getPriorityIcon(ticket.priority)}
                          <span className="ml-1">{getPriorityDisplay(ticket.priority)}</span>
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {ticket.description}
                      </p>
                      <div className="mt-2 text-xs text-gray-500">
                        {getTypeDisplay(ticket.type)} • Tạo: {formatDate(ticket.created_at)}
                      </div>
                    </div>
                    <div className="ml-4 text-right">
                      <div className="text-xs text-gray-500">
                        {(ticket.comments?.length ?? 0)} bình luận
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Support;
